import { convertEmptyStringsToNull, convertIncomingPercentage, convertOutgoingPercentage } from "@shared/modulePayload";
import { PackagingEntryData, PackagingEntryResponse } from "@features/modules/packaging/types";
import { EntryCreatePayload, EntryDeletePayload, EntryUpdatePayload, ParentUpdatePayload, ParentResponse, ParentPayload } from "../../../types/modulesInterfaces";
import { FEModules } from "../../../utils/moduleList";
import { FEModulesObj } from "../../../utils/moduleList";
import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import { createEntriesGetApi, createModuleGetApi } from "./factories/createModuleApi";

// Define constants for the module
const MODULE_TYPE = "Packaging";
const TAG: TagTypes = "Packagings";
const ENDPOINT = FEModulesObj[FEModules.Packaging].path;

const ENTRY_MODULE_TYPE = "PackagingEntries";
const TAG_ENTRY: TagTypes = "PackagingEntries";
const TAG_ENTRY_DEFAULTS: TagTypes = "PackagingEntriesDefaults";
export const ENTRY_ENDPOINT = "packaging-entries";

const packagingApiSlice = createModuleGetApi<ParentResponse>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
});

const transformEntriesResponse = (response: PackagingEntryData[]) => {
	response.forEach(entry => convertIncomingPercentage(entry, ["transmission_loss_t2"]));
	return response.sort((a, b) => a.id - b.id);
};

const packagingEntriesApiSlice = createEntriesGetApi<PackagingEntryData[]>({
	apiSlice,
	path: ENTRY_ENDPOINT,
	moduleName: ENTRY_MODULE_TYPE,
	tag: TAG_ENTRY,
	tagDefaults: TAG_ENTRY_DEFAULTS,
	transformResponse: transformEntriesResponse,
});

const publicPackagingApiSlice = createModuleGetApi<ParentResponse>({
	apiSlice: publicApiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
});

const publicPackagingEntriesApiSlice = createEntriesGetApi<PackagingEntryData[]>({
	apiSlice: publicApiSlice,
	path: ENTRY_ENDPOINT,
	moduleName: ENTRY_MODULE_TYPE,
	tag: TAG_ENTRY,
	tagDefaults: TAG_ENTRY_DEFAULTS,
	transformResponse: transformEntriesResponse,
});

export const { useGetPackagingQuery } = packagingApiSlice;
export const { useGetPackagingEntriesQuery, useGetPackagingEntriesDefaultsQuery } = packagingEntriesApiSlice;

export const { useGetPackagingQuery: usePublicGetPackagingQuery } = publicPackagingApiSlice;
export const { 
	useGetPackagingEntriesQuery: usePublicGetPackagingEntriesQuery, 
	useGetPackagingEntriesDefaultsQuery: usePublicGetPackagingEntriesDefaultsQuery 
} = publicPackagingEntriesApiSlice;

export const mutationApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		updatePackagings: builder.mutation<ParentResponse, ParentUpdatePayload<ParentPayload>>({
			query: ({ body }) => {
				const { id, ...rest } = body;
				convertEmptyStringsToNull(rest);
				return { url: `${ENDPOINT}/${id}/`, method: 'PUT', body: rest };
			},
			invalidatesTags: (_results, _error, { activityId, body }) => [
				{ type: 'Packagings', id: body?.id ?? 0 },
				{ type: "ModulesStatuses", id: activityId },
				{ type: 'ModuleResults', id: body?.id ? `packagings-${body.id}` : undefined },
				{ type: 'ModuleHistory', id: body?.id ? `packagings-${body.id}` : undefined },
			]
		}),
		updatePackagingEntry: builder.mutation<PackagingEntryResponse, EntryUpdatePayload<PackagingEntryResponse>>({
			query: ({ body }) => {
				const { id, ...rest } = body;
				convertEmptyStringsToNull(rest);
				convertOutgoingPercentage(rest, ["transmission_loss_t2"]);
				return {
					url: `${ENTRY_ENDPOINT}/${id}/`,
					method: 'PUT',
					body: rest
				};
			},
			invalidatesTags: (_results, _error, { activityId, body }) => [
				{ type: 'PackagingEntries', id: activityId },
				{ type: 'PackagingEntriesDefaults', id: body?.id ? body?.id : undefined },
			]
		}),
		createPackagingEntry: builder.mutation<PackagingEntryResponse, EntryCreatePayload>({
			query: ({ activityId, parent }) => ({ 
				url: `${ENTRY_ENDPOINT}/`, 
				method: 'POST', 
				body: { activity: activityId, parent } 
			}),
			invalidatesTags: (_results, _error, { activityId, parent }) => [
				{ type: 'PackagingEntries', id: activityId },
				{ type: 'ModuleResults', id: parent ? `packagings-${parent}` : undefined },
				{ type: 'ModuleHistory', id: parent ? `packagings-${parent}` : undefined },
				{ type: 'ModulesStatuses', id: activityId }
			]
		}),
		deletePackagingEntry: builder.mutation<void, EntryDeletePayload>({
			query: ({ id }) => ({ url: `${ENTRY_ENDPOINT}/${id}/`, method: 'DELETE' }),
			invalidatesTags: (_results, _error, { activityId, parent, id }) => [
				{ type: 'PackagingEntries', id: activityId },
				{ type: 'ModuleResults', id: parent ? `packagings-${parent}` : undefined },
				{ type: 'ModuleHistory', id: parent ? `packagings-${parent}` : undefined },
				{ type: 'PackagingEntriesDefaults', id },
				{ type: 'ModulesStatuses', id: activityId }
			]
		}),
	}),
});

export const {
	useUpdatePackagingsMutation,
	useCreatePackagingEntryMutation,
	useUpdatePackagingEntryMutation,
	useDeletePackagingEntryMutation,
} = mutationApiSlice;

export const packagingEndpoints = packagingApiSlice.endpoints;
export const packagingEntriesEndpoints = packagingEntriesApiSlice.endpoints;
export const packagingMutationEndpoints = mutationApiSlice.endpoints;